import { type FC, useEffect, useState } from 'react';
import { type Goal, type GoalProgress, type GoalsProgression } from '../types/goals';
import { goalsService } from '../services/goals';
import { AdherenceChart, GoalCard, ProgressBar, StatusBadge } from '../components/molecules';
import { Button } from '../components/atoms';

export const GoalsPage: FC = () => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [progression, setProgression] = useState<GoalsProgression | null>(null);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'overview' | 'details'>('overview');

  useEffect(() => {
    void fetchGoalsData();
  }, []);

  const fetchGoalsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch goals and progression data
      const [goalsData, progressionData] = await Promise.allSettled([
        goalsService.getAllGoals(),
        goalsService.getGoalsProgression()
      ]);

      // Handle goals data
      if (goalsData.status === 'fulfilled') {
        setGoals(goalsData.value);
      } else {
        console.warn('Failed to fetch goals:', goalsData.reason);
      }

      // Handle progression data
      if (progressionData.status === 'fulfilled') {
        setProgression(progressionData.value);
      } else {
        console.warn('Failed to fetch progression:', progressionData.reason);
        // This is optional, so we don't show an error to the user
      }

    } catch (err) {
      console.error('Failed to fetch goals data:', err);
      setError('Unable to load goals data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoalClick = (goal: Goal) => {
    setSelectedGoal(goal);
    setView('details');
  };

  const handleBackToOverview = () => {
    setSelectedGoal(null);
    setView('overview');
  };

  const getGoalProgress = (goal: Goal): GoalProgress | undefined => progression?.progresses.find(p => p.goal.id === goal.id);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your goals...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => void fetchGoalsData()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (view === 'details' && selectedGoal) {
    return <GoalDetailsView goal={selectedGoal} progression={progression} onBack={handleBackToOverview} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {progression?.title ?? 'Your Health Goals'}
              </h1>
              <p className="text-gray-600 mt-1">
                {progression?.description ?? 'Track your progress and stay motivated'}
              </p>
            </div>
            <Button onClick={() => void fetchGoalsData()}>
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Progress Overview */}
        {progression && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Progress Overview</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AdherenceChart
                weeklyAdherence={progression.weeklyAdherence}
                type="weekly"
              />
              <AdherenceChart
                monthlyAdherence={progression.monthlyAdherence}
                type="monthly"
              />
            </div>
          </div>
        )}

        {/* Goals Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Your Goals</h2>
            <div className="text-sm text-gray-500">
              {goals.length} goal{goals.length !== 1 ? 's' : ''} total
            </div>
          </div>

          {goals.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🎯</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No goals yet</h3>
              <p className="text-gray-600 mb-4">Start by adding your first health goal to track your progress.</p>
              <Button>
                Add Your First Goal
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {goals.map((goal) => {
                const progress = getGoalProgress(goal);
                return (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    progress={progress}
                    onClick={() => handleGoalClick(goal)}
                    showProgress={!!progress}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Key Activities */}
        {progression?.weeklyAdherence.keyActivities && progression.weeklyAdherence.keyActivities.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">This Week's Key Activities</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {progression.weeklyAdherence.keyActivities.map((activity, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      goalsService.getCategoryColorClass(activity.category)
                    }`}>
                      {goalsService.getCategoryDisplayName(activity.category)}
                    </span>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">{activity.title}</h4>
                  {activity.progressItems.map((item, itemIndex) => (
                    <div key={itemIndex} className="text-sm text-gray-600">
                      {item.text && <p>{item.text}</p>}
                      {item.metric && <p className="font-medium">{item.metric}</p>}
                    </div>
                  ))}
                  {activity.advice && (
                    <p className="text-sm text-blue-600 mt-2 italic">{activity.advice}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Goal Details View Component
interface GoalDetailsViewProps {
  goal: Goal;
  progression: GoalsProgression | null;
  onBack: () => void;
}

const GoalDetailsView: React.FC<GoalDetailsViewProps> = ({ goal, progression, onBack }) => {
  const [metrics, setMetrics] = useState<any[]>([]);
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  const progress = progression?.progresses.find(p => p.goal.id === goal.id);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoadingMetrics(true);
        const metricsData = await goalsService.getGoalMetrics(goal.id);
        setMetrics(metricsData);
      } catch (error) {
        console.warn('Failed to fetch goal metrics:', error);
      } finally {
        setLoadingMetrics(false);
      }
    };

    fetchMetrics();
  }, [goal.id]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button
              onClick={onBack}
              variant="secondary"
            >
              ← Back to Goals
            </Button>
          </div>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{goal.name}</h1>
              <p className="text-gray-600 mb-4">{goal.description}</p>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  goalsService.getCategoryColorClass(goal.category)
                }`}>
                  {goalsService.getCategoryDisplayName(goal.category)}
                </span>
                {progress && (
                  <StatusBadge 
                    status={progress.trending as any}
                    size="md"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Section */}
        {progress && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Current Progress</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <div className="text-sm text-gray-600 mb-1">{progress.currentTitle}</div>
                <div className="text-2xl font-bold text-gray-900">{progress.currentValue}</div>
                {progress.deltaValue && (
                  <div className={`text-sm font-medium ${
                    progress.deltaValue.startsWith('+') ? 'text-green-600' : 
                    progress.deltaValue.startsWith('-') ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {progress.deltaValue} from last measurement
                  </div>
                )}
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Progress to Goal</div>
                <ProgressBar
                  progress={progress.progress}
                  showLabel
                  variant={
                    progress.progress >= 80 ? 'success' :
                    progress.progress >= 60 ? 'default' :
                    progress.progress >= 40 ? 'warning' : 'danger'
                  }
                  size="lg"
                  animated
                />
                <div className="text-sm text-gray-600 mt-2">{progress.targetText}</div>
              </div>
            </div>

            {progress.advice && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">{progress.advice.title}</h3>
                <p className="text-blue-800">{progress.advice.body}</p>
              </div>
            )}
          </div>
        )}

        {/* Sub-goals */}
        {goal.subGoals && goal.subGoals.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Related Goals</h2>
            <div className="space-y-3">
              {goal.subGoals.map((subGoal) => (
                <div key={subGoal.id} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-1">{subGoal.name}</h3>
                  <p className="text-sm text-gray-600">{subGoal.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metrics */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Metrics & Tracking</h2>
          {loadingMetrics ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2" />
              <p className="text-gray-600">Loading metrics...</p>
            </div>
          ) : metrics.length === 0 ? (
            <p className="text-gray-600">No metrics available for this goal.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {metrics.map((metric) => (
                <div key={metric.id} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-1">{metric.name}</h3>
                  <p className="text-sm text-gray-600 mb-2">{metric.description}</p>
                  {metric.unit && (
                    <span className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                      Unit: {metric.unit}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};